import React, { useEffect, useRef, useState } from "react";
import { api, formatDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, ZoomIn, ZoomOut, RotateCcw, Loader2, FileText, ExternalLink } from "lucide-react";

/**
 * Shared full-screen preview of an EC8A result sheet.
 * Fetches the result by id, renders sheet_path via authed /api/files/{path},
 * supports zoom/pan for images and inline embed for PDFs.
 * Used by Timeline (and any future admin/reviewer surface).
 */
export const SheetPreviewModal = ({ resultId, onClose }) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState({ scale: 1, x: 0, y: 0 });
  const dragRef = useRef({ dragging: false, startX: 0, startY: 0, baseX: 0, baseY: 0 });
  const boxRef = useRef(null);
  const token = typeof window !== "undefined" ? localStorage.getItem("ec_token") : "";
  const backend = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!resultId) return;
    setLoading(true);
    api.get(`/results/${resultId}`)
      .then(r => setResult(r.data))
      .catch(e => { toast.error(formatDetail(e.response?.data?.detail) || "Could not load result"); onClose?.(); })
      .finally(() => setLoading(false));
  }, [resultId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const handler = (e) => {
      if (!result?.sheet_path || isPdf) return;
      e.preventDefault();
      setZoom(z => ({ ...z, scale: Math.max(1, Math.min(6, z.scale + (e.deltaY > 0 ? -0.2 : 0.2))) }));
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, [result?.sheet_path]);

  const resetZoom = () => setZoom({ scale: 1, x: 0, y: 0 });
  const zoomBy = (delta) => setZoom(z => ({ ...z, scale: Math.max(1, Math.min(6, z.scale + delta)) }));
  const onMouseDown = (e) => {
    if (zoom.scale <= 1) return;
    dragRef.current = { dragging: true, startX: e.clientX, startY: e.clientY, baseX: zoom.x, baseY: zoom.y };
  };
  const onMouseMove = (e) => {
    if (!dragRef.current.dragging) return;
    setZoom(z => ({ ...z, x: dragRef.current.baseX + (e.clientX - dragRef.current.startX), y: dragRef.current.baseY + (e.clientY - dragRef.current.startY) }));
  };
  const onMouseUp = () => { dragRef.current.dragging = false; };

  const sheet = result?.sheet_path;
  const isPdf = sheet && sheet.toLowerCase().endsWith(".pdf");
  const src = sheet ? `${backend}/api/files/${sheet}?auth=${token}` : "";

  return (
    <div
      onClick={onClose}
      data-testid="sheet-preview-modal"
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6">
      <div onClick={(e)=>e.stopPropagation()} className="relative max-w-6xl w-full max-h-full bg-[#111114] border border-white/10 shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400 flex items-center gap-2">
              <FileText size={11}/> EC8A Result Sheet · {result?.status || "…"}
            </div>
            <div className="text-sm mt-0.5 text-zinc-200 truncate">
              {loading ? "Loading…" : (result ? <>Submitted by <span className="text-white">{result.submitted_by_email}</span> · {(result.submitted_at||"").replace("T"," ").slice(0,16)}</> : "—")}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {!isPdf && sheet && (
              <>
                <button onClick={() => zoomBy(0.4)} data-testid="sheet-zoom-in" className="p-1.5 border border-white/10 hover:bg-white/10 text-zinc-300" title="Zoom in"><ZoomIn size={14}/></button>
                <button onClick={() => zoomBy(-0.4)} data-testid="sheet-zoom-out" className="p-1.5 border border-white/10 hover:bg-white/10 text-zinc-300" title="Zoom out"><ZoomOut size={14}/></button>
                <button onClick={resetZoom} data-testid="sheet-zoom-reset" className="p-1.5 border border-white/10 hover:bg-white/10 text-zinc-300" title="Reset"><RotateCcw size={14}/></button>
                <span className="text-[10px] tabular text-zinc-500 px-2">{Math.round(zoom.scale * 100)}%</span>
              </>
            )}
            {sheet && (
              <a href={src} target="_blank" rel="noreferrer" data-testid="sheet-open-new-tab"
                className="p-1.5 border border-white/10 hover:bg-white/10 text-zinc-300" title="Open in new tab"><ExternalLink size={14}/></a>
            )}
            <button onClick={onClose} data-testid="sheet-preview-close"
              className="p-1.5 border border-white/10 hover:bg-red-500 hover:text-white text-zinc-300 ml-1" title="Close (Esc)"><X size={14}/></button>
          </div>
        </div>

        <div ref={boxRef}
             onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
             className="relative overflow-hidden bg-[#0A0A0A] flex items-center justify-center select-none" style={{ minHeight: "60vh", maxHeight: "80vh" }}>
          {loading && <Loader2 className="animate-spin text-zinc-500" size={22}/>}
          {!loading && !sheet && <div className="text-zinc-500 text-sm p-8 text-center">No sheet was attached to this submission.</div>}
          {!loading && sheet && isPdf && (
            <iframe title="sheet" src={src} data-testid="sheet-preview-pdf"
              className="w-full" style={{ height: "80vh" }} />
          )}
          {!loading && sheet && !isPdf && (
            <img
              src={src}
              alt="EC8A sheet"
              draggable={false}
              data-testid="sheet-preview-image"
              style={{ transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.scale})`, transformOrigin: "center center", transition: dragRef.current.dragging ? "none" : "transform 120ms ease-out", cursor: zoom.scale > 1 ? (dragRef.current.dragging ? "grabbing" : "grab") : "zoom-in" }}
              className="max-w-full max-h-[80vh] object-contain"
              onDoubleClick={() => zoom.scale > 1 ? resetZoom() : zoomBy(1)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SheetPreviewModal;
